import {useQuery} from '@aboutyou/data-fetcher';
import {useDefaultCookieDomain} from '@aboutyou/storage';
import {removeCookie} from '@aboutyou/utils';
import {USER_CACHE_KEY} from '../../CacheKeys';
import {
  getUserDataFromCookies,
  setUserDataToCookies,
} from '../../utils/read-write-user-data';
import {BroadcastUserDataToTabs} from '../SyncSessionBetweenTabs';
import {createDataStore} from '../createDataStore';
import {TADARIDA_USER_PERSIST_KEY} from './constants';
import {UserState} from './types';

export const cleanupUserPersistedState = ({
  cookieDomain,
}: {
  cookieDomain?: string;
}) => removeCookie({name: TADARIDA_USER_PERSIST_KEY, domain: cookieDomain});

const useExpensiveUser = () => {
  const cookieDomain = useDefaultCookieDomain();

  const query = useQuery(
    USER_CACHE_KEY,
    async (): Promise<UserState> => {
      const userDataFromCookies = getUserDataFromCookies<UserState>({
        storageKey: TADARIDA_USER_PERSIST_KEY,
        cookieDomain,
      });

      return userDataFromCookies ?? {};
    },
    {
      onSuccess: (userState: UserState) => {
        return onUserSuccess({userState, cookieDomain});
      },
    },
  );

  return query;
};

const onUserSuccess = ({
  userState,
  cookieDomain,
}: {
  userState: UserState;
  cookieDomain?: string;
}) => {
  BroadcastUserDataToTabs.broadcastData({
    key: TADARIDA_USER_PERSIST_KEY,
    data: userState,
  });

  if (
    userState &&
    (('token' in userState && userState.token) || 'guestToken' in userState)
  ) {
    setUserDataToCookies({
      storageKey: TADARIDA_USER_PERSIST_KEY,
      data: userState,
      cookieDomain,
    });
  }
};

const {Provider, useData: useUser} = createDataStore<UserState>({
  defaultValue: {},
  useQueryHook: useExpensiveUser,
});

export {Provider as UserProvider, useUser};
